import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import axios from "axios";
import { message } from "antd";
import SearchIcon from "@mui/icons-material/Search";
import "./AdminWalletHistory.css";

const LIMIT_DEFAULT = 100;

const toStr = (v) => (v == null ? "" : String(v));
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const safeDateTime = (value) => {
  if (!value) return "-";
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return "-";
  return new Date(t).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminWalletHistory = () => {
  const [loading, setLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(LIMIT_DEFAULT);
  const [total, setTotal] = useState(0);

  // ✅ simple email search (optional)
  const [searchQuery, setSearchQuery] = useState("");

  const pages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: "Bearer " + token } : {};
  };

  const fetchHistory = async (nextPage = page) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return message.error("Token missing. Please login again.");

      setLoading(true);

      const email = searchQuery.trim();

      const res = await axios.get("/api/admin/wallet-history", {
        headers: authHeaders(),
        params: {
          page: nextPage,
          limit, // ✅ always 100
          ...(email ? { email } : {}),
        },
        timeout: 20000,
      });

      if (res.data?.success) {
        const data = res.data?.data || {};
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(safeNum(data.total));
        setPage(safeNum(data.page) || nextPage);
      } else {
        message.error(res.data?.message || "Failed to fetch wallet history");
      }
    } catch (err) {
      console.log(err);
      message.error(err?.response?.data?.message || "Failed to fetch wallet history");
    } finally {
      setLoading(false);
    }
  };

  // load latest 100
  useEffect(() => {
    fetchHistory(1);
    // eslint-disable-next-line
  }, []);

  const onRefresh = () => {
    setPage(1);
    fetchHistory(1);
  };

  const onNext = () => {
    if (page < pages) fetchHistory(page + 1);
  };

  const onPrev = () => {
    if (page > 1) fetchHistory(page - 1);
  };

  const onSearch = () => {
    setPage(1);
    fetchHistory(1);
  };

  return (
    <AdminLayout>
      <div className="admin-wh-container">
        {/* ✅ same header style as AdminUsers */}
        <div className="page-title">
          <div className="title-left">
            <h3 className="m-0">Wallet History</h3>
            <h6 className="m-0">
              Total Records - {total} (Showing {limit} / Page {page} of {pages})
            </h6>
          </div>
        </div>

        <hr />

        {/* ✅ same container style */}
        <div className="table-container">
          {/* ✅ tools bar like AdminUsers */}
          <div className="tools">
            <div className="form-fields">
              <SearchIcon className="text-dark me-2" />
              <input
                type="search"
                name="search"
                placeholder="Search by email (optional)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSearch();
                }}
              />
            </div>

            <button className="whBtn whApply" onClick={onSearch} disabled={loading}>
              Search
            </button>
          </div>

          {loading ? <div className="wh-loading">Loading...</div> : null}

          {/* ✅ scroll wrapper (mobile safe) */}
          <div className="table-scroll">
            <table className="table user-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Email</th>
                  <th>User ID</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Net</th>
                  <th>Balance After</th>
                  <th>Reason</th>
                  <th>Note</th>
                  <th>Txn ID</th>
                  <th>Admin ID</th>
                </tr>
              </thead>

              <tbody>
                {!items.length ? (
                  <tr>
                    <td colSpan={11} style={{ textAlign: "center", padding: 18, opacity: 0.7 }}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  items.map((it) => {
                    const t = toStr(it?.type).toUpperCase();
                    const isCredit = t === "CREDIT";

                    return (
                      <tr key={toStr(it?._id)}>
                        <td>
                          <small>{safeDateTime(it?.createdAt)}</small>
                        </td>
                        <td>
                          <small>{toStr(it?.email) || "-"}</small>
                        </td>
                        <td>
                          <small>{toStr(it?.user_oid) || "-"}</small>
                        </td>
                        <td>
                          <span className={`whTag ${isCredit ? "whCredit" : "whDebit"}`}>
                            {t || "-"}
                          </span>
                        </td>
                        <td>
                          <small>{safeNum(it?.amount)}</small>
                        </td>
                        <td>
                          <small>{safeNum(it?.net)}</small>
                        </td>
                        <td>
                          <small>{safeNum(it?.balance_after)}</small>
                        </td>
                        <td>
                          <small>{toStr(it?.reason) || "-"}</small>
                        </td>
                        <td>
                          <small title={toStr(it?.note)}>
                            {toStr(it?.note) ? toStr(it.note).slice(0, 24) : "-"}
                            {toStr(it?.note).length > 24 ? "..." : ""}
                          </small>
                        </td>
                        <td>
                          <small title={toStr(it?.txn_id)}>
                            {toStr(it?.txn_id) ? toStr(it.txn_id).slice(0, 18) : "-"}
                            {toStr(it?.txn_id).length > 18 ? "..." : ""}
                          </small>
                        </td>
                        <td>
                          <small>{toStr(it?.by_admin_oid) || "-"}</small>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* bottom pager */}
          <div className="pagination-container">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
              <button className="whBtn" onClick={onPrev} disabled={loading || page <= 1}>
                Prev
              </button>
              <button className="whBtn whApply" onClick={onNext} disabled={loading || page >= pages}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminWalletHistory;
